import { useEffect, useState } from "react";
import Backend from "../../../../Backend/Backend";
import toast from "react-hot-toast";
import useSize from "../../../../Utils/useSize/useSize";
import Loading from "../../../../Utils/Loading/Loading";
import NotFound from "../../../../Utils/NotFound/NotFound";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ChevronsDownUp, CircleCheck, Circle } from "lucide-react";
import Header from "./Header";

const backend = await new Backend().init();

export default function EditSymbol() {
  const windowSize = useSize();
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    // Load cached watchlist
    const cachedWatchlist = localStorage.getItem("watchlist");

    if (cachedWatchlist) {
      setWatchlist(JSON.parse(cachedWatchlist));
      setLoading(false);
    }

    // Always refresh from API
    loadSymbols();
  }, []);

  const loadSymbols = () => {
    const data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      user_id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      watchlist_id: "1",
    };

    backend.watchlist(data).then((r) => {
      if (r.error === "False") {
        const newData = r.watchlist;

        const cached = localStorage.getItem("watchlist");

        if (
          !cached ||
          JSON.stringify(JSON.parse(cached)) !== JSON.stringify(newData)
        ) {
          setWatchlist(newData);
          localStorage.setItem("watchlist", JSON.stringify(newData));
        }
      } else {
        if (r.logout === 1) window.LogOut();
        else toast.error(r.message);
      }
    });
  };

  // DRAG HANDLER
  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(watchlist);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    setWatchlist(items);

    const data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      user_id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      watchlist_id: "1",
      watchlist: items,
    };

    backend.edit_symbol(data).then((r) => {
      setLoading(false);
      if (r.error === "False") {
      } else {
        if (r.logout === 1) window.LogOut();
        else toast.error(r.message);
      }
    });
  };

  // SELECT HANDLER
  const toggleSelect = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter((s) => s !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  // SELECT ALL HANDLER
  const toggleSelectAll = () => {
    if (selected.length === watchlist.length) {
      setSelected([]);
    } else {
      setSelected(watchlist.map((item) => item.id));
    }
  };

  // DELETE SELECTED
  const deleteSelected = () => {
    if (selected.length === 0) return toast.error("No symbols selected!");

    const updated = watchlist.filter((item) => !selected.includes(item.id));
    localStorage.setItem("watchlist", JSON.stringify([]));
    setWatchlist(updated);
    setSelected([]);
    toast.success("Selected symbols deleted!");

    handleDeleteOptions();
  };

  const handleDeleteOptions = async () => {
    if (loading) return;
    setLoading(true);

    const data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      user_id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      symbol_id: selected,
      symboltype: "selected",
    };

    try {
      const r = await backend.delete_symbol(data);
      setLoading(false);
      if (r.error === "False") {
        const newData = JSON.stringify(r.watchlist);
        await localStorage.setItem("watchlist", newData);
      } else toast.error(r.message);
    } catch {
      setLoading(false);
      toast.error("Failed to remove symbol");
    }
  };

  return (
    <div className="flex flex-col bg-[var(--background)]">
      <Header
        selected={selected}
        toggleSelectAll={toggleSelectAll}
        deleteSelected={deleteSelected}
      />

      {/* MAIN LIST */}
      <div style={{ height: windowSize[1] - 70 }} className="overflow-y-scroll">
        {loading ? (
          <Loading height={windowSize[1] - 70} />
        ) : watchlist.length === 0 ? (
          <NotFound
            small
            height={windowSize[1] - 70}
            title="Watchlist empty"
            subtext="Add symbols to get started."
          />
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="symbols">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="pb-32"
                >
                  {watchlist.map((item, index) => (
                    <Draggable
                      key={item.id}
                      draggableId={String(item.id)}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          className={`flex items-center justify-between p-3 
                            borderb-2 border-[var(--hover)] mb-2 
                            bg-[var(--background)] shadow-sm transition 
                            ${
                              snapshot.isDragging
                                ? "scale-[1.02] shadow-md"
                                : ""
                            }
                          `}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          {/* SELECT CHECKBOX */}
                          <div
                            onClick={() => toggleSelect(item.id)}
                            className="cursor-pointer"
                          >
                            {selected.includes(item.id) ? (
                              <CircleCheck className="text-[var(--buymeta)]" />
                            ) : (
                              <Circle className="text-[var(--hover)]" />
                            )}
                          </div>

                          {/* SYMBOL NAME */}
                          <div className="flex-1 ml-3">
                            <p className="text-[var(--text)] font-semibold">
                              {item.symbol_display}.
                            </p>
                          </div>

                          {/* DRAG ICON */}
                          <div className="cursor-grab text-[var(--subtext)]">
                            <ChevronsDownUp />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}

                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </div>
  );
}
